import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { getOptionsQueryParams } from "@app/class/Utilities";
import { HardwareSource } from "@app/enum/HardwareSource";
import { ILatestSensorDataValuesQueryOptions } from "@app/interfaces/ILatestSensorDataValuesQueryOptions";
import { ISensorData } from "@app/interfaces/ISensorData";
import { ILatestSensorDataValues, ISensorDataValue, LatestSensorDataValues, SensorDataValueType } from "@app/interfaces/ISensorDataValue";
import { IVehicle } from "@app/interfaces/IVehicle";
import { UnitConversionService, UnitConversionType } from "@app/services/unitConversion.service";
import { VehicleService } from "@app/services/vehicle.service";
import { lastValueFrom } from "rxjs";
import { IVehicleWithSensorDataValues } from "./_models/IVehicleWithSensorData";

export interface IUserFriendlySensorData extends ISensorDataValue {
    Label: string;
    Type: SensorDataValueType;
}

@Injectable({ providedIn: 'root' })
export class SensorDataService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly vehicleService: VehicleService,
        private readonly unitConversionService: UnitConversionService
    ) { }

    getLatestSensorData(invalidateCache = false): Promise<ISensorData[]> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<ISensorData[]>("/api/sensorData/latest", options));
    }

    getLatestSensorDataByVehicle(vehicleId: number, invalidateCache = false): Promise<ISensorData> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<ISensorData>(`/api/sensorData/latest/${vehicleId}`, options));
    }

    getLatestSensorDataValues(queryOptions: ILatestSensorDataValuesQueryOptions = {}): Promise<LatestSensorDataValues[]> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = queryOptions.minsToLive;
        options.params["invalidateCache"] = queryOptions.invalidateCache;
        options.params["VehicleId"] = queryOptions.VehicleId;

        return lastValueFrom(this.httpClient
            .get<ILatestSensorDataValues[]>(`/api/sensorData/latest/values`, options))
            .then(data => data
                .filter(value => value && value[SensorDataValueType.VehicleId])
                .map(value => new LatestSensorDataValues(value)));
    }

    getSensorData(vehicleId: number, start: Date, end: Date, invalidateCache = false): Promise<ISensorData[]> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;
        options.params["start"] = start.toISOString();
        options.params["end"] = end.toISOString();

        return lastValueFrom(this.httpClient.get<ISensorData[]>(`/api/sensorData/${vehicleId}`, options));
    }

    getLatestVehiclesSensorData(invalidateCache = false): Promise<IVehicleWithSensorDataValues[]> {
        const vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true, invalidateCache }) as Promise<IVehicle[]>;
        const sensorDataPromise = this.getLatestSensorDataValues({ invalidateCache });

        return Promise
            .all([vehiclesPromise, sensorDataPromise])
            .then(([vehicles, sensorData]) => {
                return vehicles
                    .map(vehicle => {
                        const matchingSensorData = sensorData.find(s => s.vehicleId === vehicle.Id);

                        return matchingSensorData
                            ? { ...vehicle, latestSensorDataValues: matchingSensorData } as IVehicleWithSensorDataValues
                            : null;
                    })
                    .filter((vehicle) => vehicle !== null)
            })
    }

    getEngineHours(latestSensorDataValues: LatestSensorDataValues): number {
        const engineHours = latestSensorDataValues?.getValue<number>(SensorDataValueType.PrimaryAuxEngineOnDuration);

        return engineHours ? engineHours / 3600 : null;
    }

    getUserFriendlySensorDataValues(latestSensorDataValues: LatestSensorDataValues): IUserFriendlySensorData[] {
        var values: IUserFriendlySensorData[] = [];

        for (const [stringKey, value] of Object.entries(latestSensorDataValues.values)) {
            var sensorValue = value as ISensorDataValue;
            var key = Number(stringKey) as SensorDataValueType;

            if (!this.isValueTypeUserFriendly(key) || !this.isValidData(key, sensorValue.Value)) {
                continue;
            }

            values.push({
                Type: key,
                EventTime: sensorValue.EventTime,
                Value: sensorValue.Value,
                Label: this.getLabel(key),
                ValueLabel: this.getValueLabel(key, sensorValue.Value),
                Icon: this.getIcon(key)
            })
        }

        return values;
    }

    getLabel(key: SensorDataValueType): string {
        var string = SensorDataValueType[key];
        string = string.replace("Km", "");

        return string.split(/(?=[A-Z])/).join(" ")
    }

    isValueTypeUserFriendly(type: SensorDataValueType): boolean {

        switch (type) {
            case SensorDataValueType.VehicleId:
                return false;

            default:
                return true;
        }
    }

    // --

    private getValueLabel(type: SensorDataValueType, value: any): string {
        var numberValue = Number(value);

        switch (type) {
            case SensorDataValueType.AdBlueLevel:
            case SensorDataValueType.FuelLevel:
            case SensorDataValueType.PrimaryAuxEngineFuelLevel:
                if (Math.round(value) < 1) {
                    return "N/A";
                }

                return `${Math.round(value)}%`;

            case SensorDataValueType.TotalWeight:
                return `${numberValue.toLocaleString()} Kg`;

            case SensorDataValueType.PrimaryAuxEngineOnDuration:
            case SensorDataValueType.PrimaryAuxElectricOnDuration:
                return `${(numberValue / 3600).toLocaleString()} hours`;

            case SensorDataValueType.OdometerKm:
                return this.unitConversionService.convertUnitWithSuffix(value /*/ 1000*/, UnitConversionType.Distance, 0);

            case SensorDataValueType.PrimaryAuxEngineSource:
                return HardwareSource[numberValue];

            default:
                return value;
        }
    }

    private getIcon(type: SensorDataValueType): string {
        switch (type) {
            case SensorDataValueType.AdBlueLevel:
            case SensorDataValueType.FuelLevel:
            case SensorDataValueType.PrimaryAuxEngineFuelLevel:
                return "fa-solid fa-duotone fa-gas-pump";

            case SensorDataValueType.TotalWeight:
                return "fa-solid fa-duotone fa-weight-hanging";

            case SensorDataValueType.PrimaryAuxEngineOnDuration:
            case SensorDataValueType.PrimaryAuxElectricOnDuration:
                return "fa-regular fa-duotone fa-cloc";

            case SensorDataValueType.OdometerKm:
                return "fa-solid fa-duotone fa-route";

            case SensorDataValueType.PrimaryAuxEngineSource:

            default:
                return "fa-solid fa-duotone fa-circle-info";
        }
    }

    private isValidData(type: SensorDataValueType, value: any): boolean {
        var numberValue = Number(value);

        switch (type) {
            case SensorDataValueType.AdBlueLevel:
            case SensorDataValueType.FuelLevel:
                return numberValue >= 0 && numberValue <= 100;

            case SensorDataValueType.Axle1Weight:
            case SensorDataValueType.Axle2Weight:
            case SensorDataValueType.Axle3Weight:
            case SensorDataValueType.Axle4Weight:
            case SensorDataValueType.TotalWeight:
                return numberValue > 0;

            default:
                return true;
        }
    }
}
